import React from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import { IconCamera } from "@tabler/icons-react";

interface TakeSnapshotControlProps {
  isOpen: boolean;
  loading: boolean;
  onSubmit: (snapshotName: string) => Promise<boolean> | boolean;
}

function TakeSnapshotControl({
  isOpen,
  loading,
  onSubmit,
}: TakeSnapshotControlProps) {
  const { t } = useTranslation();
  const [isTakingSnapshot, setIsTakingSnapshot] = React.useState(false);
  const [snapshotName, setSnapshotName] = React.useState("");

  React.useEffect(() => {
    if (!isOpen) {
      setIsTakingSnapshot(false);
      setSnapshotName("");
    }
  }, [isOpen]);

  const resetForm = () => {
    setIsTakingSnapshot(false);
    setSnapshotName("");
  };

  const handleTakeSnapshot = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedSnapshotName = snapshotName.trim();
    if (!trimmedSnapshotName) return;
    const didSucceed = await onSubmit(trimmedSnapshotName);
    if (didSucceed) {
      resetForm();
    }
  };

  if (isTakingSnapshot) {
    return (
      <form onSubmit={handleTakeSnapshot} className="flex items-center gap-2">
        <input
          autoFocus
          type="text"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm h-10 px-3"
          placeholder={t("mappingEditor.enterSnapshotName")}
          value={snapshotName}
          onChange={(e) => setSnapshotName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              resetForm();
            }
          }}
        />
        <Button
          type="primary"
          width="fit-content"
          htmlType="submit"
          loading={loading}
          disable={!snapshotName.trim()}
        >
          {t("mappingEditor.save")}
        </Button>
        <Button type="gray" width="fit-content" onClick={resetForm}>
          {t("mappingEditor.cancel")}
        </Button>
      </form>
    );
  }

  return (
    <Button
      type="gray"
      width="fit-container"
      leadingIcon={<IconCamera size={18} />}
      onClick={() => setIsTakingSnapshot(true)}
      loading={loading}
    >
      {t("mappingEditor.createManualSnapshot")}
    </Button>
  );
}

export default TakeSnapshotControl;
